import { CoverageOption, Driver, Tractor, Trailer } from "@deathstar/types/northstar";
import { DriverRow, EquipmentRow, formatLicense } from "@deathstar/types/waypoint";
import { ActionButton, DataTable, fuzzyFilter, Tabs, useSnackbar } from "@deathstar/ui";
import { DocumentCurrencyDollarIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { animated, useSpring } from "react-spring";
import api from "../../api/api";
import { FEATURE_FLAGS, isFeatureEnabled } from "../../util/featureFlags";
import { useAccountId } from "../../api/useAccountId";
import Loader from "../../components/loader/Loader";
import { queryClient } from "../../util/queryClient";
import { DriverColumns, DriverTableMeta } from "./DriverColumns";
import { NewDriverDialog } from "./NewDriverDialog";
import { OwnerOperatorEmployerLinker } from "./OwnerOperatorEmployerLinker";
import { TractorLinker, TrailerLinker } from "./TractorLinker";
import {
    getDriverChangeDetail,
    getPendingDriverChanges,
    hasPendingRemoval,
    getPendingDriverChangeValues,
    getPendingDriverPolicyChangePriority,
    getPendingPolicyChangeRowClass,
    isPendingPolicyAdditionRow,
    usePendingPolicyChanges,
    withPendingDriverAdditions,
} from "../pending-policy-changes/PendingPolicyChanges";

const AnimatedDataTable = animated(DataTable<DriverRow>);

export default function DriversTable({ data }: { data: DriverRow[] | undefined }) {
    const accountId = useAccountId();
    const { data: companyName } = api.organization.useById(accountId!, { select: (org) => org.company.name });
    const [filter, setFilter] = useState<"all" | "company" | "owner-operator">("all");
    const [adding, setAdding] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [linkingTractorToDriverId, setLinkingTractorToDriverId] = useState<number | null>(null);
    const [linkingTrailerToDriverId, setLinkingTrailerToDriverId] = useState<number | null>(null);
    const [linkingEmployerToDriverId, setLinkingEmployerToDriverId] = useState<number | null>(null);
    const updateDriver = api.drivers.useUpdate(accountId!, {
        onError: (error) => {
            useSnackbar.add(error.message, { variant: "error" });
        },
    });
    const pendingPolicyChanges = usePendingPolicyChanges();
    const memoizedData = useMemo(() => data || [], [data]);
    const tableData = useMemo(
        () => withPendingDriverAdditions(memoizedData, pendingPolicyChanges.changes, accountId!),
        [accountId, memoizedData, pendingPolicyChanges.changes]
    );

    const spring = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        pause: !data,
    });

    const sortingState = useMemo(() => {
        return [{ id: "pending", desc: true }, ...sorting];
    }, [sorting]);

    const columnFilters = useMemo(() => {
        if (filter === "all") {
            return [];
        }
        return [{ id: "owner-operator", value: filter === "owner-operator" }];
    }, [filter]);

    const columnVisibility = useMemo<VisibilityState>(() => ({ "owner-operator": filter !== "company" }), [filter]);

    const meta: DriverTableMeta = useMemo(
        () => ({
            openTractorLinker(driverId: number) {
                setLinkingTractorToDriverId(driverId);
            },
            openTrailerLinker(driverId: number) {
                setLinkingTrailerToDriverId(driverId);
            },
            openEmployerLinker(driverId: number) {
                setLinkingEmployerToDriverId(driverId);
            },
            companyName: companyName || "",
            isPendingRemoval(row: { accountId: string; id: number }) {
                return hasPendingRemoval(getPendingDriverChanges(pendingPolicyChanges.changes, row.accountId, row.id));
            },
        }),
        [companyName, pendingPolicyChanges.changes]
    );

    const columns = useMemo(() => {
        return (
            [
                DriverColumns.mobileCell,
                DriverColumns.browserPending,
                {
                    ...DriverColumns.pending,
                    accessorFn: (row: DriverRow) => getPendingDriverPolicyChangePriority(pendingPolicyChanges.changes, row),
                },
                DriverColumns.upcomingDate,
                DriverColumns.nameColumn,
                DriverColumns.licenseColumn,
                DriverColumns.dobColumn,
                DriverColumns.dohColumn,
                DriverColumns.expColumn,
                DriverColumns.ownerOperatorColumn,
                DriverColumns.coveragesColumn,
                DriverColumns.linkedTractorsColumn,
                DriverColumns.linkedTrailersColumn,
                tableData.some((r) =>
                    r.coverages?.some(
                        (c) =>
                            (!c.isUpcoming && c.coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT) ||
                            c.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY
                    )
                ) && DriverColumns.employerColumn,
                DriverColumns.actionsColumn,
            ] as ColumnDef<DriverRow>[]
        ).filter(Boolean);
    }, [pendingPolicyChanges.changes, tableData]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: fuzzyFilter,
        state: {
            sorting: sortingState,
            columnFilters,
            columnVisibility,
        },
        initialState: {
            pagination: {
                pageSize: 30,
            },
        },
        onSortingChange: setSorting,
        meta,
    });

    const downloadCSV = useMutation({
        mutationFn: async () => {
            const tableData = memoizedData.filter((r) => {
                switch (filter) {
                    case "company":
                        return !r.policyOwnerOperator;
                    case "owner-operator":
                        return r.policyOwnerOperator;
                    default:
                        return true;
                }
            });

            if (tableData.length === 0) {
                useSnackbar.add("No data to export", { variant: "info" });
                return;
            }

            const tractors = await api.equipment.findTractors(tableData[0].accountId);
            const trailers = await api.equipment.findTrailers(tableData[0].accountId);

            const rows: string[] = [];
            tableData.forEach((row) => {
                rows.push(
                    [
                        row.name,
                        row.license,
                        row.dob,
                        row.policyDoh,
                        row.policyExp,
                        row.policyOwnerOperator ? "Ind. contractor" : "Company",
                        row.coverages
                            ?.filter((c) => !c.isUpcoming)
                            .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                            .join(",") || "",
                        row.tractors
                            ?.map((t1) => tractors.find((t2) => t1.id === t2.id) as EquipmentRow<Tractor>)
                            .filter(Boolean)
                            .map((t) => t.unitNumber || t.vin?.slice(-4))
                            .filter(Boolean)
                            .map((x) => "#" + x)
                            .join(",") || "",
                        row.trailers
                            ?.map((t1) => trailers.find((t2) => t1.id === t2.id) as EquipmentRow<Trailer>)
                            .filter(Boolean)
                            .map((t) => t.unitNumber || t.vin?.slice(-4))
                            .filter(Boolean)
                            .map((x) => "#" + x)
                            .join(",") || "",
                        row.employer?.name,
                    ]
                        .map((x) => `"${x ?? ""}"`)
                        .join(",")
                );
            });

            const csv = [
                [
                    "Name",
                    "License",
                    "DOB",
                    "DOH",
                    "EXP",
                    "Type",
                    "Coverage",
                    "Tractors",
                    "Trailers",

                    tableData.some((r) =>
                        r.coverages
                            ?.filter((c) => !c.isUpcoming)
                            .some(
                                (c) =>
                                    c.coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT ||
                                    c.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY
                            )
                    ) && "Employer",
                ]
                    .filter(Boolean)
                    .join(","),
                ...rows,
            ].join("\n");

            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.download = "drivers.csv";
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
    });

    // the client-side build is behind a manual localStorage flag while it rolls out; see featureFlags.ts
    const useClientSideICReport = isFeatureEnabled(FEATURE_FLAGS.newICReport);
    const generateIndependentContractorReport = api.monthlyReports.useGenerateIndependentContractorReport(accountId!);
    const generateICReportClientSide = api.monthlyReports.useGenerateIndependentContractorReportClientSide(
        accountId!,
        companyName || ""
    );
    const isICReportPending = generateICReportClientSide.isPending || generateIndependentContractorReport.isPending;

    const runICReport = () => {
        // typed as Error so it is accepted by both mutations, whose error types differ
        const onError = (e: Error) => useSnackbar.add(e.message || "Could not generate report", { variant: "error" });
        if (useClientSideICReport) {
            generateICReportClientSide.mutate(undefined, { onError });
        } else {
            generateIndependentContractorReport.mutate(undefined, { onError });
        }
    };

    return (
        <div>
            {data ? (
                <>
                    <AnimatedDataTable
                        style={spring}
                        table={table}
                        getRowClassName={(row) =>
                            isPendingPolicyAdditionRow(row)
                                ? "bg-emerald-100/80"
                                : getPendingPolicyChangeRowClass(
                                      getPendingDriverChanges(pendingPolicyChanges.changes, row.accountId, row.id)
                                  )
                        }
                        headerRow={
                            <div className="flex flex-wrap items-end gap-4">
                                <Tabs<typeof filter>
                                    tabs={[
                                        {
                                            label: () => <p className="px-2 py-1 md:w-32">All drivers</p>,
                                            value: "all",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                        {
                                            label: () => <p className="px-2 py-1 md:w-32">Company</p>,
                                            value: "company",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                        {
                                            label: () => <p className="px-2 py-1 md:w-64">Independent contractors</p>,
                                            value: "owner-operator",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                    ]}
                                    value={filter}
                                    onChange={setFilter}
                                    className="rounded-md bg-stone-100 py-1 shadow-inner"
                                    borderClasses="bg-white border border-waypoint-blue z-[0] shadow rounded-md"
                                />
                                <div className="grow"></div>
                                {memoizedData.some((r) =>
                                    r.coverages
                                        ?.filter((c) => !c.isUpcoming)
                                        .some(
                                            (c) =>
                                                c.coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT ||
                                                c.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY
                                        )
                                ) && (
                                    <ActionButton
                                        onClick={runICReport}
                                        disabled={isICReportPending}
                                    >
                                        {isICReportPending ? (
                                            <CircularProgress size="1rem" />
                                        ) : (
                                            <DocumentCurrencyDollarIcon className="h-5 w-5" />
                                        )}
                                        Get IC report
                                    </ActionButton>
                                )}
                                <ActionButton disabled={downloadCSV.isPending} onClick={() => downloadCSV.mutate()}>
                                    {downloadCSV.isPending ? <CircularProgress size="1rem" /> : <TableCellsIcon className="h-5 w-5" />}
                                    Export
                                </ActionButton>
                                <ActionButton onClick={() => setAdding(true)}>
                                    <PlusIcon className="h-5 w-5" />
                                    Add driver
                                </ActionButton>
                                <DataTable.Search table={table} />
                            </div>
                        }
                    />
                    <NewDriverDialog
                        open={adding}
                        onClose={() => setAdding(false)}
                        defaultValues={{ atDate: moment().format("YYYY-MM-DD") }}
                        getExistingDriver={(license: string) => {
                            return memoizedData.find((r) => formatLicense(r.license) === formatLicense(license)) as Driver;
                        }}
                        deferPolicyChanges
                    />
                    <TractorLinker
                        open={!!linkingTractorToDriverId}
                        currentValue={memoizedData.find((r) => r.id === linkingTractorToDriverId)?.tractors || []}
                        onClose={() => setLinkingTractorToDriverId(null)}
                        onChange={async (tractors) => {
                            if (linkingTractorToDriverId) {
                                updateDriver.mutate({ id: linkingTractorToDriverId, tractors });
                                setLinkingTractorToDriverId(null);
                            }
                        }}
                    />
                    <TrailerLinker
                        open={!!linkingTrailerToDriverId}
                        currentValue={memoizedData.find((r) => r.id === linkingTrailerToDriverId)?.trailers || []}
                        onClose={() => setLinkingTrailerToDriverId(null)}
                        onChange={async (trailers) => {
                            if (linkingTrailerToDriverId) {
                                updateDriver.mutate({ id: linkingTrailerToDriverId, trailers });
                                setLinkingTrailerToDriverId(null);
                            }
                        }}
                    />
                    <OwnerOperatorEmployerLinker
                        open={!!linkingEmployerToDriverId}
                        currentValue={(() => {
                            const id = linkingEmployerToDriverId;
                            const driver = memoizedData.find((row) => row.id === id);
                            const employer = id
                                ? getPendingDriverChangeValues(pendingPolicyChanges.changes, accountId!, id).employer
                                : undefined;
                            if (employer === null) return null;
                            if (typeof employer === "number") return employer;
                            return driver?.employerId ?? null;
                        })()}
                        onClose={() => setLinkingEmployerToDriverId(null)}
                        onChange={async (employer) => {
                            if (linkingEmployerToDriverId) {
                                const driver = memoizedData.find((r) => r.id === linkingEmployerToDriverId);
                                if (driver?.policyOwnerOperator && !employer) {
                                    useSnackbar.add("Independent contractors must have an employer address.", { variant: "error" });
                                    return;
                                }
                                const id = linkingEmployerToDriverId;
                                const originalValues = { employer: driver?.employerId ?? null };
                                pendingPolicyChanges.addChange({
                                    accountId: accountId!,
                                    kind: "drivers",
                                    label: "Change driver employer",
                                    detail: getDriverChangeDetail(driver?.name),
                                    submit: async () => {
                                        await api.drivers.update(accountId!, id, { employer: employer || null });
                                        if (employer && typeof employer === "object") {
                                            await queryClient.invalidateQueries({
                                                queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!),
                                            });
                                            await queryClient.invalidateQueries({ queryKey: api.certificates.queryKeys.find(accountId!) });
                                        }
                                    },
                                    unitUpdate: { kind: "drivers", id, changes: { employer: employer || null }, originalValues },
                                });
                                setLinkingEmployerToDriverId(null);
                            }
                        }}
                        driver={memoizedData.find((r) => r.id === linkingEmployerToDriverId)!}
                    />
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
}
